import { RouteGroupItemWaypoint } from '@ekt-group/general-purpose-api-interfaces';
import { ChevronDown, ChevronRight, Image03, Maximize02, PlusSquare } from '@untitled-ui/icons-react';
import { Modal, Skeleton, TableProps } from 'antd';
import dayjs from 'dayjs';
import { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IconExtraService } from '@/components/icons';
import Button from '@/components/ui/Button';
import { DATE_FORMAT } from '@/constants';
import { WaypointPicture } from '@/features/service-history/api/use-get-waypoint-pictures';
import { formatAsCurrency } from '@/helpers/number-formatter';
import { useBreakpoint } from '@/hooks/use-breakpoints';
import { cn } from '@/utils/cn';

import { IExtraService, WaypointStatus } from '../types/waypoint';

type ColumnsType<T> = TableProps<T>['columns'];

interface UseWaypointsTableProps {
  waypointPhotosQuery: {
    data: WaypointPicture[];
    isLoading: boolean;
  };
  expandedRowKeys: string[];
  handleExpand: (key: string, event: MouseEvent, expandable: boolean) => void;
}

export function useWaypointsTable({ waypointPhotosQuery, expandedRowKeys, handleExpand }: UseWaypointsTableProps) {
  const { t, i18n } = useTranslation('serviceHistoryPage');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { isBelowLg } = useBreakpoint('lg');
  const { isAboveMd, isBelowMd } = useBreakpoint('md');
  const { isBelowSm } = useBreakpoint('sm');
  const { isBelowXs } = useBreakpoint('xs');

  const [selectedImage, setSelectedImage] = useState<string>();

  const handleExpandImageClick = (photo: string) => {
    setIsModalOpen(true);
    setSelectedImage(photo);
  };

  const getWaypointStatus = (waypoint: RouteGroupItemWaypoint) => {
    if (waypoint.fail && waypoint.done) {
      return WaypointStatus.Fail;
    }
    if (!waypoint.fail && waypoint.done) {
      return WaypointStatus.Discharged;
    }
    return WaypointStatus.NotDone;
  };

  const renderWaypointStatusBadge = (waypoint: RouteGroupItemWaypoint, fullWidth: boolean = false) => {
    const status = getWaypointStatus(waypoint);
    const badgeColors = {
      [WaypointStatus.Discharged]: '#D5DC2E',
      [WaypointStatus.Fail]: '#B9B9B9',
      [WaypointStatus.NotDone]: '#F97315',
    };

    return (
      <button
        style={{
          backgroundColor: badgeColors[status],
        }}
        disabled={status !== WaypointStatus.Discharged}
        className={cn(
          'inline-flex outline-none cursor-pointer items-center justify-center px-2 rounded-[14px] h-9 font-medium',
          status === WaypointStatus.Discharged ? 'text-gray-10' : 'text-white',
          { 'w-full': fullWidth },
        )}
      >
        {t(status)}
        {status === WaypointStatus.Discharged && <ChevronRight className="w-4 h-4 stroke-2" />}
      </button>
    );
  };

  const getExtraServices = (waypoint: RouteGroupItemWaypoint) => {
    const extraServices: IExtraService[] = waypoint.waypointLogDetails
      ?.filter((log) => log.type === 'extra_service')
      .map((log) => {
        const kojvService = waypoint.services?.find((service) => service.id === log.kojvServiceId);
        return {
          date: log.issued,
          name: kojvService?.name,
          quantity: log.value,
        };
      });

    return extraServices;
  };

  const columns: ColumnsType<RouteGroupItemWaypoint> = [
    {
      key: '1',
      dataIndex: ['routeGroupItem', 'date'],
      title: t('date'),
      render: (value) => <span>{dayjs(value).format(DATE_FORMAT)}</span>,
    },
    {
      key: '1',
      dataIndex: ['objectItem', 'id'],
      title: t('objectItemId'),
    },
    {
      key: '2',
      title: t('priceList'),
      dataIndex: ['kojv', 'name'],
      hidden: !isBelowXs,
    },
    {
      key: '3',
      title: t('objectAddress'),
      dataIndex: ['objectAddress', 'address'],
      render: (address) => <span>{address || '---'}</span>,
      hidden: isBelowMd,
    },
    {
      key: '4',
      title: t('wasteType'),
      dataIndex: 'garbage',
      render: (garbage) => (
        <div className="flex gap-2 items-center">
          <img src={`/garbage/${i18n.language}/${garbage.id}.svg`} alt="garbage-icon" className="w-8 object-contain" />
          <span className="text-[#595959] font-normal">{garbage.name}</span>
        </div>
      ),
      hidden: isBelowLg,
    },
    {
      key: '5',
      title: t('containerType'),
      dataIndex: ['itemType', 'name'],
      hidden: isBelowLg,
    },
    {
      key: '6',
      align: 'center',
      render: (record) => (
        <div className="flex gap-2 pb-2 justify-center items-center">
          <Image03
            className={cn('text-[#A7AD2A]', {
              hidden: !waypointPhotosQuery.data?.filter((photo) => photo.waypointId === record.id)?.length,
            })}
          />
          <span className={cn('stroke-[#A7AD2A] text-transparent', { hidden: !getExtraServices(record)?.length })}>
            <IconExtraService size="sm" />
          </span>
          <PlusSquare className={cn('text-[#A7AD2A]', { hidden: !record.objectItemDischarge?.isExtraDischarge })} />
        </div>
      ),
      hidden: isBelowLg,
    },
    {
      key: 'price',
      title: t('price'),
      dataIndex: 'priceWithVat',
      align: 'center',
      render: (priceWithVat) => <span>{formatAsCurrency(Number(priceWithVat || 0))}</span>,
      hidden: isBelowSm,
    },
    {
      key: '7',
      title: t('status'),
      dataIndex: ['id'],
      align: 'center',
      render: (_, record) => renderWaypointStatusBadge(record),
      hidden: isBelowSm,
    },
    {
      key: 'expand',
      title: 'Details',
      width: 48,
      render: (_, record) => (
        <Button
          onClick={(event) => handleExpand(record.id.toString(), event, !isAboveMd)}
          className="h-9 w-9 ml-auto flex flex-col items-center justify-center"
          variant="primary"
        >
          {expandedRowKeys.includes(record.id.toString()) ? (
            <ChevronDown width={16} height={16} />
          ) : (
            <ChevronRight width={16} height={16} />
          )}
        </Button>
      ),
      hidden: isAboveMd,
    },
  ];

  const expandedRowRender = (
    record: RouteGroupItemWaypoint & {
      extraServicePrices?: { priceWithVat: string }[];
    },
  ) => {
    const extraServices = getExtraServices(record);
    const isDetailsVisible = Boolean(
      extraServices.length || waypointPhotosQuery.data?.filter((waypointPhoto) => waypointPhoto.waypointId === record.id)?.length,
    );

    return (
      <div
        className={cn('flex-col px-4 py-4 md:py-0 lg:divide-none divide-y divide-gray-300 flex', {
          isDetailsVisible: 'sm:hidden',
        })}
      >
        <div className="lg:hidden grid grid-cols-7 sm:pb-4 items-center sm:divide-none divide-y divide-gray-300">
          <div className="col-span-7 sm:py-0 py-4 sm:hidden flex flex-col gap-2 text-gray-10 text-sm">
            <span className="font-medium">{t('priceList')}</span>
            <span>{record.kojv?.name || '---'}</span>
          </div>
          <div className="col-span-7 sm:py-0 py-4 sm:col-span-2 md:hidden flex flex-col gap-2 text-gray-10 text-sm">
            <span className="font-medium">{t('objectAddress')}</span>
            <span>{record.object?.fullAddress || '---'}</span>
          </div>
          <div className="col-span-7 sm:py-0 py-4 sm:col-span-3 flex flex-col gap-2 text-gray-10 text-sm">
            <span className="font-medium">{t('wasteType')}</span>
            <span>{record.garbage?.name || '---'}</span>
          </div>
          <div className="col-span-7 sm:py-0 py-4 sm:col-span-2 flex flex-col gap-2 text-gray-10 text-sm">
            <span className="font-medium">{t('containerType')}</span>
            <span>{record.itemType?.name || '---'}</span>
          </div>
          <div className="col-span-7 sm:py-0 py-4 sm:col-span-2 sm:hidden flex flex-col gap-2 text-gray-10 text-sm">
            <span className="font-medium">{t('price', { ns: 'invoicesPage' })}</span>
            <span>{formatAsCurrency(Number(record.priceWithVat || 0))}</span>
          </div>
          <div className="col-span-7 sm:hidden flex items-center sm:py-0 py-4">{renderWaypointStatusBadge(record, true)}</div>
        </div>
        {isDetailsVisible && (
          <div className="grid grid-cols-7 py-2 gap-y-4 items-center">
            {Boolean(extraServices.length) && (
              <>
                <div className="col-span-3 md:col-span-2 flex flex-col gap-2 text-gray-10 text-sm">
                  <span className="font-medium">{t('extraServices')}</span>
                  <span>{extraServices[0]?.name || '---'}</span>
                </div>
                <div className="col-span-2 md:col-span-1 flex flex-col gap-2 text-gray-10 text-sm">
                  <span className="font-medium">{t('quantity')}</span>
                  <span>{extraServices[0]?.quantity || '---'}</span>
                </div>
                <div className="col-span-2 md:col-span-1 flex flex-col justify-between gap-2 text-gray-10 text-sm">
                  <span className="font-medium">{t('price', { ns: 'invoicesPage' })}</span>
                  <span>{formatAsCurrency(Number(record?.extraServicePrices?.[0]?.priceWithVat || 0))}</span>
                </div>
              </>
            )}

            <div
              className={cn('col-span-7 items-center flex gap-4 pb-4 sm:pb-0 justify-start justify-self-end md:justify-end', {
                'md:col-span-3': extraServices.length,
              })}
            >
              {waypointPhotosQuery.isLoading ? (
                <>
                  <Skeleton.Node className="w-[96px] h-[92px]" active />
                  <Skeleton.Node className="w-[96px] h-[92px]" active />
                  <Skeleton.Node className="w-[96px] h-[92px]" active />
                </>
              ) : (
                waypointPhotosQuery.data
                  ?.filter((waypointPhoto) => waypointPhoto.waypointId === record.id)
                  ?.map((waypointPhoto) => (
                    <>
                      <div onClick={() => handleExpandImageClick(waypointPhoto.photo)} className="relative cursor-pointer">
                        <img
                          className="rounded-[14px] w-[96px] h-[92px] object-cover"
                          width={96}
                          height={92}
                          src={waypointPhoto.photo}
                          alt="waypoint-photo"
                        />
                        <button className="absolute outline-none flex items-center justify-center bottom-[6px] right-[6px] w-8 h-8 rounded-[8px] bg-white">
                          <Maximize02 className="w-6 h-6 stroke-2 text-[#B9B9B9]" />
                        </button>
                      </div>
                      <Modal closeIcon={<></>} open={isModalOpen} footer={null} onCancel={() => setIsModalOpen(false)} centered>
                        <img src={selectedImage} alt="waypoint-photo" className="w-full h-auto rounded-lg" />
                      </Modal>
                    </>
                  ))
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return {
    columns,
    expandedRowRender,
  };
}
